/** Where to look for what a CFO decision waits for. Plain links the operator
 *  opens in a new tab: nothing is fetched from here, nothing is sent anywhere. */

import type {
  CfoExplainedInput,
  CfoResearchBrief,
  CfoResearchFounder,
  CfoScoreExplanation,
} from "@/api/types";
import { countryName, inputLabel } from "@/lib/cfoLabels";

/** The inputs each block of the research sheet is about. */
export const FOUNDER_DIMENSIONS = ["founder_geography"];
export const FUNDING_DIMENSIONS = [
  "is_excluded_incubator",
  "funding",
  "funding_stage_group",
  "is_tech_sector",
  "is_blacklisted_sector",
];
export const TEAM_DIMENSIONS = ["finance_team_level", "has_hr_lead", "has_cos_coo"];
export const JOB_DIMENSIONS = ["hiring_finance_level", "hiring_hr", "hiring_stratops"];

/** INFO codes that stand for a missing scoring input. */
const INFO_DIMENSIONS: Record<string, string> = { founder_unconfirmed: "founder_geography" };

/** What the decision waits for now, from the current actions: the lot's list is the
 *  evaluation at the time of the reservation, and each answer re-scores the company.
 *  Null while re-scoring, and for a company to review (its actions are places, not the
 *  missing inputs), where the lot's list stands. */
export function currentMissing(
  list: { state: string; decision?: string | null; actions?: { id: string; dimension?: string | null }[] } | null,
): string[] | null {
  if (!list || list.state !== "CURRENT" || list.decision === "NEEDS_REVIEW") return null;
  if (list.decision !== "NEEDS_DATA") return [];
  return [
    ...new Set(
      (list.actions ?? []).map((a) => INFO_DIMENSIONS[a.dimension ?? ""] ?? a.dimension ?? a.id),
    ),
  ];
}

/** What the Founders block waits for, said precisely: a founder to find or confirm, a
 *  career to read, or the items that keep a US-only career open. Owner, 2026-09-28:
 *  "Missing: Founder place" read as missing founders when the co-founders were there. */
export interface FoundersNeed {
  /** Short, on the block's pill. */
  label: string;
  /** One sentence in the block, when there is more to say. */
  detail?: string;
}

const HOME = "US";

export function foundersNeed(
  brief: CfoResearchBrief | null,
  missing: string[],
  review: boolean,
  /** A school question is asked in the actions above. */
  asked = false,
): FoundersNeed | null {
  if (review) return { label: "A founder place to review" };
  if (!missing.includes("founder_geography")) return null;
  if (!brief) return { label: "Missing: founder places" };
  const founders = brief.founders;
  if (!founders.length) return { label: "No founder identified" };
  const who = (list: CfoResearchFounder[]) =>
    list.map((f) => f.name ?? "a founder").join(", ");
  const abroad = (f: CfoResearchFounder) => [
    ...new Set(
      f.places
        .filter((p) => p.established && p.country && p.country !== HOME)
        .map((p) => countryName(p.country as string)),
    ),
  ];
  // A founder no one confirmed yet, whose place abroad would count once confirmed.
  const waiting = founders.filter(
    (f) => f.status !== "CONFIRMED" && f.founder && abroad(f).length > 0,
  );
  const confirm = waiting.length
    ? ` Confirm ${who(waiting)}: their place in ${waiting.flatMap(abroad).join(", ")} would count.`
    : "";
  const confirmed = founders.filter((f) => f.status === "CONFIRMED");
  if (!confirmed.length) {
    return { label: "No confirmed founder", detail: confirm.trim() || undefined };
  }
  const count = `${confirmed.length} confirmed founder${confirmed.length > 1 ? "s" : ""}`;
  const unread = confirmed.filter((f) => !f.career);
  const partial = confirmed.filter((f) => f.career && !f.career.complete);
  if (unread.length || partial.length) {
    const parts = [
      unread.length ? `the career of ${who(unread)} was not read` : "",
      partial.length ? `the career of ${who(partial)} was read only in part` : "",
    ].filter(Boolean);
    return {
      label: unread.length ? "Career not read" : "Career read in part",
      detail:
        `${count}; ${parts.join("; ")}. ` +
        `Document a place from their profile: one outside the US would count.${confirm}`,
    };
  }
  const blocking = [
    ...new Set(confirmed.flatMap((f) => f.places.filter((p) => p.blocks).map((p) => p.place ?? p.item ?? ""))),
  ].filter(Boolean);
  const onlyHome = confirmed.every((f) =>
    Object.keys(f.career?.placed ?? {}).every((c) => c === HOME),
  );
  if (blocking.length) {
    const shown = blocking.slice(0, 3).join(", ") + (blocking.length > 3 ? "…" : "");
    return {
      label: `${blocking.length} ${blocking.length > 1 ? "places" : "place"} to identify`,
      detail:
        `${count}, careers read` +
        (onlyHome ? ": every place read is in the US" : "") +
        `. Not placed yet: ${shown}. Until they are, the company cannot be closed as ` +
        `US-only; a place outside the US would count.` +
        (asked ? " Say where the school is in the question above." : "") +
        confirm,
    };
  }
  return { label: "Missing: founder places", detail: `${count}, careers read.${confirm}` };
}

/** "Missing: Finance team, HR lead": what the decision waits for from a block. */
export function needOf(dimensions: string[], missing: string[]): string | null {
  const labels = dimensions.filter((d) => missing.includes(d)).map(inputLabel);
  return labels.length ? `Missing: ${labels.join(", ")}` : null;
}

/** The explained input of each dimension, from the score explanation. */
export function inputsByDimension(
  explanation: CfoScoreExplanation | null,
): Record<string, CfoExplainedInput> {
  const found: Record<string, CfoExplainedInput> = {};
  for (const rule of explanation?.rules ?? []) {
    for (const input of rule.inputs ?? []) {
      found[input.dimension] ??= input;
    }
  }
  return found;
}

export interface ResearchLink {
  label: string;
  href: string;
  hint?: string;
}

const google = (query: string) =>
  `https://www.google.com/search?q=${encodeURIComponent(query)}`;

const quoted = (value: string) => `"${value.replace(/"/g, "")}"`;

export function companyLinks(company: {
  name: string;
  domain?: string | null;
  website?: string | null;
  crunchbase_url?: string | null;
}): ResearchLink[] {
  const name = quoted(company.name);
  return [
    ...(company.website ? [{ label: "Website", href: company.website }] : []),
    {
      label: "Crunchbase",
      href: company.crunchbase_url ?? google(`${name} site:crunchbase.com`),
      hint: "Investors, rounds, people",
    },
    {
      label: "LinkedIn",
      href: `https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(company.name)}`,
      hint: "Company page and its people",
    },
    {
      label: "Google",
      href: google(`${name}${company.domain ? ` ${company.domain}` : ""}`),
    },
  ];
}

export function founderLinks(founder: string, company: string): ResearchLink[] {
  return [
    {
      label: "LinkedIn profile",
      href: google(`${quoted(founder)} ${quoted(company)} site:linkedin.com/in`),
    },
    {
      label: "Education",
      href: google(`${quoted(founder)} (university OR school OR MBA OR alumni)`),
    },
    {
      label: "Interviews & bio",
      href: google(`${quoted(founder)} ${quoted(company)} (founder OR interview OR about)`),
    },
  ];
}

export function foundersSearch(company: string): ResearchLink {
  return {
    label: "Find the founders",
    href: google(`${quoted(company)} (founder OR co-founder OR CEO) site:linkedin.com/in`),
  };
}

/** The team inputs: who already holds the finance, HR or operations role. */
export const TEAM_SEARCHES: Record<string, { label: string; terms: string }> = {
  finance_team_level: {
    label: "Finance people",
    terms: '(CFO OR "VP Finance" OR "Head of Finance" OR controller OR accountant OR "FP&A")',
  },
  has_hr_lead: {
    label: "HR people",
    terms: '("Head of People" OR "HR Manager" OR "People Operations" OR recruiter OR "Talent")',
  },
  has_cos_coo: {
    label: "COO, Chief of Staff",
    terms: '(COO OR "Chief of Staff" OR "Head of Operations" OR "VP Operations")',
  },
};

export function teamLink(dimension: string, company: string): ResearchLink | null {
  const search = TEAM_SEARCHES[dimension];
  if (!search) return null;
  return {
    label: search.label,
    href: google(`${quoted(company)} ${search.terms} site:linkedin.com/in`),
  };
}

/** The hiring inputs: open positions in finance, HR or strategy and operations. */
export const JOB_SEARCHES: Record<string, { label: string; terms: string }> = {
  hiring_finance_level: {
    label: "Finance openings",
    terms: '(finance OR accountant OR controller OR "FP&A" OR CFO)',
  },
  hiring_hr: { label: "HR openings", terms: '(HR OR "People" OR recruiter OR talent)' },
  hiring_stratops: {
    label: "Strategy & ops openings",
    terms: '("Chief of Staff" OR operations OR "BizOps" OR strategy)',
  },
};

export function jobLinks(company: string, domain?: string | null): ResearchLink[] {
  return [
    {
      label: "Careers page",
      href: google(`${quoted(company)} (careers OR jobs)${domain ? ` site:${domain}` : ""}`),
    },
    {
      label: "LinkedIn jobs",
      href: `https://www.linkedin.com/jobs/search/?keywords=${encodeURIComponent(company)}`,
    },
    {
      label: "Job boards",
      href: google(
        `${quoted(company)} (site:greenhouse.io OR site:lever.co OR site:ashbyhq.com OR site:wellfound.com)`,
      ),
    },
  ];
}

export function jobLink(dimension: string, company: string): ResearchLink | null {
  const search = JOB_SEARCHES[dimension];
  if (!search) return null;
  return { label: search.label, href: google(`${quoted(company)} ${search.terms} job`) };
}

export function investorsLink(company: string): ResearchLink {
  return {
    label: "Investors & accelerators",
    href: google(`${quoted(company)} (investors OR "backed by" OR accelerator OR incubator)`),
  };
}
